import { type VariantProps } from 'class-variance-authority';
import { Plus, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';

import { chipVariants } from './variants';

export interface ChipProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof chipVariants> {
  removable?: boolean;
  onRemove?: () => void;
  removeIcon?: 'x' | 'plus';
  /**
   * Accessible name for the remove control. Defaults to a bare "Remove", which
   * is only adequate for a lone chip — a row of them announces the same name
   * for every destructive button, so any caller rendering a list should pass
   * a translated "Remove {label}".
   */
  removeLabel?: string;
}
export const Chip = React.forwardRef<HTMLDivElement, ChipProps>(function Chip(
  {
    className,
    tone,
    size,
    removable,
    onRemove,
    removeIcon = 'x',
    removeLabel = 'Remove',
    children,
    ...props
  },
  ref,
) {
  const Icon = removeIcon === 'plus' ? Plus : X;
  const iconColorClass =
    removeIcon === 'plus' ? 'text-red-400' : 'text-gray-500';

  return (
    <div
      ref={ref}
      className={cn(chipVariants({ tone, size }), className)}
      {...props}
    >
      <span className="leading-none">{children}</span>
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="inline-flex h-4 w-4 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
          aria-label={removeLabel}
        >
          <Icon className={cn('h-4 w-4', iconColorClass)} />
        </button>
      )}
    </div>
  );
});

Chip.displayName = 'Chip';
